/**
 * Algorithm Selector Component - JavaScript Version
 * 
 * A reusable component for selecting pathfinding algorithms.
 * Provides a clean interface with algorithm information and selection.
 */

import React from 'react';
import './AlgorithmSelector.css';

const AlgorithmSelector = ({
  algorithms,
  selectedAlgorithm,
  onAlgorithmChange,
  isLoading = false,
  disabled = false,
  className = ''
}) => {
  const handleAlgorithmChange = (algorithmId) => {
    if (!disabled && !isLoading) {
      onAlgorithmChange(algorithmId);
    }
  };

  const getAlgorithmButtonClass = (algorithm) => {
    const baseClass = 'algorithm-btn';
    const isSelected = selectedAlgorithm === algorithm.id;
    const isDisabled = disabled || isLoading || !algorithm.isAvailable;
    
    let classes = [baseClass];
    
    if (isSelected) classes.push('active');
    if (isDisabled) classes.push('disabled');
    if (!algorithm.isAvailable) classes.push('unavailable');
    
    return classes.join(' ');
  };

  const renderAlgorithmInfo = (algorithm) => (
    <div className="algorithm-info">
      <div className="algorithm-header">
        <strong className="algorithm-name">{algorithm.name}</strong>
        <span className="algorithm-complexity">{algorithm.complexity}</span>
      </div>
      <p className="algorithm-description">{algorithm.description}</p>
      {algorithm.suitableFor && algorithm.suitableFor.length > 0 && (
        <div className="algorithm-suitable-for">
          <span className="suitable-label">Suitable for:</span>
          <div className="suitable-tags">
            {algorithm.suitableFor.map((tag, index) => (
              <span key={index} className="suitable-tag">{tag}</span>
            ))}
          </div>
        </div>
      )}
      {algorithm.limitations && algorithm.limitations.length > 0 && (
        <div className="algorithm-limitations">
          <span className="limitations-label">Limitations:</span>
          <ul className="limitations-list">
            {algorithm.limitations.map((limitation, index) => (
              <li key={index} className="limitation-item">{limitation}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className={`algorithm-selector loading ${className}`}>
        <div className="loading-message">
          <span className="loading-spinner">⏳</span>
          Loading algorithms...
        </div>
      </div>
    );
  }

  if (algorithms.length === 0) {
    return (
      <div className={`algorithm-selector empty ${className}`}>
        <div className="empty-message">
          <span className="empty-icon">📋</span>
          No algorithms available
        </div>
      </div>
    );
  }

  return (
    <div className={`algorithm-selector ${className}`}>
      <h3 className="selector-title">Select Algorithm</h3>
      <div className="algorithm-grid">
        {algorithms.map((algorithm) => (
          <button
            key={algorithm.id}
            className={getAlgorithmButtonClass(algorithm)}
            onClick={() => handleAlgorithmChange(algorithm.id)}
            disabled={disabled || isLoading || !algorithm.isAvailable}
            title={!algorithm.isAvailable ? 'Algorithm not available' : ''}
          >
            {renderAlgorithmInfo(algorithm)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AlgorithmSelector;
